'use client'

import { cn } from '@/lib/utils'

import { InlineLink } from '@/components/page-kit'

import { EASE, HAIRLINE, QuietLink, SectionHead, useReveal } from './shared'

/* The capability list, set as a specification rather than a bullet list: each
   line is display type on its own ruled row, with the module that owns it set
   quietly opposite. There is not a single checkmark on this page — a tick in a
   circle beside eight lines of 16px body copy is the shape the shared template
   uses, and it reads as a marketing checklist rather than a scope document.

   The owner column is the point: it turns the list into something a director
   can actually circulate, because every line has a department against it. */

const SPEC = [
  {
    line: 'End-to-end student lifecycle, enquiry to alumnus',
    owner: 'Core platform',
  },
  { line: 'Integrated LMS for digital academic delivery', owner: 'LMS' },
  { line: 'Faculty and staff management, hire to exit', owner: 'HRMS' },
  { line: 'Automated payroll with statutory compliance', owner: 'Payroll' },
  { line: 'Accounts, fee governance and audit trail', owner: 'Accounts' },
  { line: 'Role-based access enforced at the query', owner: 'Core platform' },
  { line: 'Real-time analytics and leadership dashboards', owner: 'AI Engine' },
  { line: 'Mobile-first experience on every surface', owner: 'Every module' },
]

export function UdcSpec() {
  const list = useReveal<HTMLDivElement>(0.1)

  return (
    <section
      id="specification"
      className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28"
    >
      <div className="container mx-auto">
        <SectionHead
          eyebrow="Scope"
          title="What ships on day one."
          deck="Not a roadmap. This is the platform as delivered, with the module that owns each line set against it."
          action={
            <QuietLink href="/product-brochure">
              Download the brochure
            </QuietLink>
          }
        />

        <div ref={list.ref} className={cn('mt-10 border-t sm:mt-12', HAIRLINE)}>
          {SPEC.map((s, i) => (
            <div
              key={s.line}
              className={cn(
                'grid grid-cols-[2rem_1fr] items-baseline gap-x-3 border-b py-5 sm:grid-cols-[3rem_1fr_auto] sm:gap-x-6 sm:py-6',
                HAIRLINE
              )}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(10px)',
                transition: `opacity 500ms ${EASE} ${i * 70}ms, transform 500ms ${EASE} ${i * 70}ms`,
              }}
            >
              <span className="text-[11px] tabular-nums text-muted-foreground/60 sm:text-xs">
                {String(i + 1).padStart(2, '0')}
              </span>

              <p className="font-heading text-lg font-normal leading-snug text-foreground sm:text-xl lg:text-2xl">
                {s.line}
              </p>

              <span className="col-start-2 mt-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground sm:col-start-3 sm:mt-0 sm:text-right sm:text-[11px]">
                {s.owner}
              </span>
            </div>
          ))}
        </div>

        {/* The owner column names four modules that each have a page of their
            own, and the K-12 build of the same platform has one too. */}
        <p className="mt-8 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          Each owner above has its own record:{' '}
          <InlineLink href="/products/hrms">HRMS</InlineLink>,{' '}
          <InlineLink href="/products/payroll">payroll</InlineLink>,{' '}
          <InlineLink href="/products/accounts">accounts</InlineLink> and the{' '}
          <InlineLink href="/products/lms">
            learning management system
          </InlineLink>
          . Schools running K-12 operations get the same platform as{' '}
          <InlineLink href="/schoolerp">Euclideum School ERP</InlineLink>, with{' '}
          <InlineLink href="/udise-apaar">
            UDISE+ and APAAR ID reporting
          </InlineLink>{' '}
          included.
        </p>
      </div>
    </section>
  )
}
